
/** Splunk `eventType` when `postMessage` data is not valid JSON / object (no iframe envelope to read). */
export const PAYMENT_MINI_APP_SPLUNK_EVENT_POSTMESSAGE_PARSE_FAILURE =
  "IFRAME_POSTMESSAGE_PARSE_FAILURE";

/** Splunk `eventType` when parsed `postMessage` is not a plain object. */
export const PAYMENT_MINI_APP_SPLUNK_EVENT_POSTMESSAGE_INVALID_PAYLOAD =
  "IFRAME_POSTMESSAGE_INVALID_PAYLOAD";

function hasOwnKey(obj, key) {
  return (
    obj != null &&
    typeof obj === "object" &&
    Object.prototype.hasOwnProperty.call(obj, key)
  );
}

/**
 * DTO-style fields shared by payment mini-app Splunk payloads (reference context).
 *
 * @param {object} input
 * @param {string} [input.payLinkOrderId]
 * @param {object} [input.userInfo]
 * @param {object} [input.userDetails]
 */
export function getPaymentMiniAppLogDto({
  payLinkOrderId = "",
  userInfo = {},
  userDetails = {},
}) {
  const ui = userInfo || {};
  const ud = userDetails || {};
  return {
    orderId: payLinkOrderId || "",
    email: ud.email || ui.email || "",
    Screen: "SecurePaymentPortal",
    CustomerGuid: hasOwnKey(ui, "authId") ? ui.authId : "",
    countryCode: hasOwnKey(ud, "countryCode")
      ? ud.countryCode
      : hasOwnKey(ui, "countryCode")
        ? ui.countryCode
        : "",
    Source_System: "HDX",
  };
}

/** Splunk facet name for Payment Mini App rows (consistent with Wrapper `Functionality`). */
const PAYMENT_MINI_APP_SPLUNK_FUNCTIONALITY = "Payment Mini App";

/**
 * Splunk facets common to Payment Mini App events (portal + reference context).
 * Pass {@link getPaymentMiniAppLogDto} output — same shape as Redux/user snapshot used for Splunk.
 *
 * @param {object} [logDto]
 * @returns {{ Functionality: string; Source_System: string; Screen: string; CustomerGuid: string; countryCode: string; orderId: string; email: string }}
 */
export function getCommonSplunkDtoForPaymentMiniApp(logDto) {
  const d =
    logDto != null && typeof logDto === "object" ? logDto : {};
  return {
    Functionality: PAYMENT_MINI_APP_SPLUNK_FUNCTIONALITY,
    Source_System: "HDX",
    Screen: "SecurePaymentPortal",
    CustomerGuid: d.CustomerGuid || "",
    countryCode: d.countryCode || "",
    orderId: d.orderId || "",
    email: d.email || "",
  };
}

export function normalizePiParityLogMetadata(metadata, fallbackOrderId) {
  const m = metadata && typeof metadata === "object" ? metadata : {};
  return {
    ...m,
    orderId:
      m.orderId !== undefined && m.orderId !== null
        ? m.orderId
        : fallbackOrderId || undefined,
  };
}

/**
 * Telemetry / business event ids for the payment mini-app and related secure-portal flows.
 * Wallet iframe `Notification` entries use the same string the iframe sends (`body.type`).
 */
export const PAYMENT_MINI_APP_EVENTS = Object.freeze({
  USER_LOGIN: "SECURE_PORTAL_USER_LOGIN",
  PAYMENT_MINI_APP_LOADED: "PAYMENT_MINI_APP_LOADED",
  PAYMENT_SELECTION_CHANGED: "PAYMENT_SELECTION_CHANGED",
  ACCEPT_TERM_AND_CONDITIONS: "ACCEPT_TERM_AND_CONDITIONS",
  PAYMENT_SUBMITTED: "PAYMENT_SUBMITTED",
  REDIRECTED_TO_VENDOR: "REDIRECTED_TO_VENDOR",
  EXTERNAL_WALLET_REDIRECT_INITIATED: "EXTERNAL_WALLET_REDIRECT_INITIATED",
  THREE_DS_REDIRECT_INITIATED: "3DS_REDIRECT_INITIATED",
  LPM_REDIRECT_INITIATED: "LPM_REDIRECT_INITIATED",
  PAYMENT_SUCCESS: "PAYMENT_SUCCESS",
  PAYMENT_FAILURE: "PAYMENT_FAILURE",
  PAYMENT_CANCELLED: "PAYMENT_CANCELLED",
});
export const PAYMENT_SELECTION_CHANGED_FORM_TYPE_SEPARATOR =
  "-";

/** Backend-only status rows — must not be created by the UI. */
export const BACKEND_STATUS_EVENT_TYPES = Object.freeze([
  PAYMENT_MINI_APP_EVENTS.PAYMENT_SUCCESS,
  PAYMENT_MINI_APP_EVENTS.PAYMENT_FAILURE,
]);

export function isBackendOnlyStatusEventType(eventType) {
  return BACKEND_STATUS_EVENT_TYPES.includes(eventType);
}

export function isPaymentSelectionChangedEventType(eventType) {
  const type = String(eventType || "");
  return (
    type === PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED ||
    type.indexOf(
      `${PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED}${PAYMENT_SELECTION_CHANGED_FORM_TYPE_SEPARATOR}`,
    ) === 0
  );
}

function isBackendStatusEvent(event) {
  return (
    event != null &&
    typeof event === "object" &&
    isBackendOnlyStatusEventType(event.eventType)
  );
}

function isConsecutiveDuplicateSelection(last, event) {
  return (
    last &&
    event &&
    isPaymentSelectionChangedEventType(event.eventType) &&
    last.eventType === event.eventType
  );
}

/** Collapses back-to-back identical payment-method rows (iframe / reconcile duplicates). */
export function collapseConsecutivePaymentSelectionDuplicates(events) {
  return (Array.isArray(events) ? events : []).filter((event, index, arr) => {
    if (!event || typeof event !== "object") {
      return false;
    }
    if (index === 0) {
      return true;
    }
    return !isConsecutiveDuplicateSelection(arr[index - 1], event);
  });
}

export const DEFAULT_IFRAME_HEIGHT_PX = 800;
export const MIN_IFRAME_HEIGHT_PX = 120;

export const MIN_IFRAME_WIDTH_PX = 100;

export function clampIframeHeightPx(raw) {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return null;
  }
  const rounded = Math.round(raw);
  if (rounded < MIN_IFRAME_HEIGHT_PX) {
    return null;
  }
  return rounded;
}

export function clampIframeWidthPx(raw) {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return null;
  }
  const rounded = Math.round(raw);
  if (rounded < MIN_IFRAME_WIDTH_PX) {
    return null;
  }
  return rounded;
}

/** Allowed `window.open` target names — avoids arbitrary strings / window.name injection. */
const SAFE_WINDOW_OPEN_TARGETS = new Set(["_blank", "_self", "_parent", "_top"]);

/**
 * Opens `data.externalUrl` from an OpenURL / EXTERNAL iframe message (https only).
 * @param {object} eventData — Parsed postMessage payload
 */
export function openExternalUrlFromOpenUrlEvent(eventData) {
  const d =
    eventData.data && typeof eventData.data === "object" ? eventData.data : {};
  const rawUrl = d.externalUrl;
  if (typeof rawUrl !== "string") {
    return;
  }
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return;
  }
  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return;
  }
  if (url.protocol !== "https:") {
    return;
  }
  const tRaw = typeof d.target === "string" ? d.target.trim() : "_blank";
  const target = SAFE_WINDOW_OPEN_TARGETS.has(tRaw) ? tRaw : "_blank";
  if (target === "_blank") {
    window.open(url.href, "_blank", "noopener,noreferrer");
  } else {
    window.open(url.href, target);
  }
}

function mergeSearchParams(target, searchParams) {
  searchParams.forEach((value, key) => {
    target[key] = value;
  });
}

/**
 * Reads query params from both window.location.search and the hash (HashRouter).
 */
export function getUrlQueryParams() {
  const out = {};
  mergeSearchParams(out, new URLSearchParams(window.location.search));
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q !== -1) {
    mergeSearchParams(out, new URLSearchParams(hash.slice(q + 1)));
  }
  return out;
}

/**
 * Payment mini-app (pay-link iframe): requires Floodgate flag plus orderId in the URL.
 */
export function isPaymentMiniApp() {
  const p = getUrlQueryParams();
  const orderId = (p.orderId || "").trim();
  const active = orderId.length > 0;
  return { active, orderId: active ? orderId : null };
}

/**
 * Parses wallet notification `body` (stringified JSON), supporting both
 * `event.data.data.body` and `event.data.body`.
 */
export function parseWalletNotificationBody(event) {
  let body = {};
  const raw = event?.data?.data?.body ?? event?.data?.body;
  if (typeof raw === "string") {
    try {
      body = JSON.parse(raw || "{}");
    } catch {
      body = {};
    }
  } else if (raw && typeof raw === "object") {
    body = raw;
  }
  return body && typeof body === "object" ? body : {};
}

function pickTrackingMeta(body) {
  if (!body || typeof body !== "object") {
    return {};
  }
  const keys = [
    "paymentType",
    "orderId",
    "errorCode",
    "fromPaymentFormType",
    "toPaymentFormType",
    "timestamp",
  ];
  return keys.reduce((acc, key) => {
    const v = body[key];
    if (v !== undefined && v !== null) {
      acc[key] = v;
    }
    return acc;
  }, {});
}

/**
 * Normalizes Hendrix `SPP_PAYMENT_EVENTS` fetch response to an event record array.
 *
 * @param {{ status?: number; data?: object | null }} response
 * @returns {ReadonlyArray<{ eventType: string; status: string }>}
 */
export function mapPaymentMiniAppStatusEventsResponse(response) {
  if (!response || response.status === 204 || response.data == null) {
    return [];
  }
  const data = response.data;
  if (!data || typeof data !== "object") {
    return [];
  }
  let message = data.message;
  if (message == null || message === "") {
    return [];
  }
  if (typeof message === "string") {
    try {
      message = JSON.parse(message);
    } catch {
      return [];
    }
  }
  return Array.isArray(message) ? message : [];
}

/**
 * Stable identity — never dedupe by event name alone.
 * @param {object} event
 * @param {number} [index]
 */
export function getPaymentEventIdentityKey(event, index = 0) {
  if (event == null || typeof event !== "object") {
    return `idx:${index}`;
  }
  if (event.eventId != null) {
    return String(event.eventId);
  }
  const type = String(event.eventType || "");
  if (event.timestamp != null) {
    return `${type}|ts:${event.timestamp}`;
  }
  if (event.sequenceNumber != null) {
    return `${type}|seq:${event.sequenceNumber}`;
  }
  if (event.orderIndex != null) {
    return `${type}|ord:${event.orderIndex}`;
  }
  return `${type}|idx:${index}`;
}

function getEventTimelineSortValue(event, fallbackIndex) {
  if (event == null || typeof event !== "object") {
    return fallbackIndex;
  }
  if (typeof event.orderIndex === "number") {
    return event.orderIndex;
  }
  if (typeof event.sequenceNumber === "number") {
    return event.sequenceNumber;
  }
  if (event.timestamp != null) {
    return Number(event.timestamp);
  }
  return fallbackIndex;
}

/**
 * Sorts out-of-order API rows by timeline metadata (scenario 11).
 * @param {ReadonlyArray<object>} events
 */
export function sortEventsByTimelineOrder(events) {
  return (Array.isArray(events) ? events : [])
    .map((event, index) => ({ event, index }))
    .sort((a, b) => {
      const aVal = getEventTimelineSortValue(a.event, a.index);
      const bVal = getEventTimelineSortValue(b.event, b.index);
      if (aVal !== bVal) {
        return aVal - bVal;
      }
      return a.index - b.index;
    })
    .map(({ event }) => event)
    .filter((event) => event && typeof event === "object");
}

/**
 * @param {object} event
 * @param {number} [index]
 */
export function normalizePaymentEventRecord(event, index = 0) {
  if (!event || typeof event !== "object") {
    return null;
  }
  const timestamp =
    event.timestamp != null ? Number(event.timestamp) : undefined;
  const orderIndex =
    typeof event.orderIndex === "number" ? event.orderIndex : index;
  const eventId =
    event.eventId != null
      ? String(event.eventId)
      : `${event.eventType}_${timestamp != null ? timestamp : orderIndex}_${index}`;
  return {
    ...event,
    ...(timestamp != null ? { timestamp } : {}),
    orderIndex,
    eventId,
  };
}

/**
 * Reconcile Redux (UI) with GET (backend). Backend is source of truth for Failed/Success.
 * Keeps all backend rows, appends Redux-only UI rows (by eventId/timestamp), then sorts.
 * @param {ReadonlyArray<object>} reduxEvents
 * @param {ReadonlyArray<object>} backendEvents
 */
export function reconcileEventTimeline(reduxEvents, backendEvents) {
  const reduxUi = (Array.isArray(reduxEvents) ? reduxEvents : [])
    .map((event, index) => normalizePaymentEventRecord(event, index))
    .filter(Boolean)
    .filter((event) => !isBackendStatusEvent(event));

  const backend = sortEventsByTimelineOrder(
    (Array.isArray(backendEvents) ? backendEvents : [])
      .map((event, index) => normalizePaymentEventRecord(event, index))
      .filter(Boolean),
  );

  if (backend.length === 0) {
    return collapseConsecutivePaymentSelectionDuplicates(reduxUi);
  }

  const backendKeys = new Set(
    backend.map((event, index) => getPaymentEventIdentityKey(event, index)),
  );
  const merged = collapseConsecutivePaymentSelectionDuplicates([...backend]);

  reduxUi.forEach((event, index) => {
    const key = getPaymentEventIdentityKey(event, index);
    if (backendKeys.has(key)) {
      return;
    }
    const lastMerged = merged[merged.length - 1];
    if (isConsecutiveDuplicateSelection(lastMerged, event)) {
      return;
    }
    merged.push(event);
    backendKeys.add(key);
  });

  return collapseConsecutivePaymentSelectionDuplicates(merged);
}

/**
 * Returns reconciled timeline and whether Redux should update.
 */
export function syncReduxWithBackendEvents(reduxEvents, backendEvents) {
  const merged = reconcileEventTimeline(reduxEvents, backendEvents);
  return {
    merged,
    hasMismatch: JSON.stringify(reduxEvents) !== JSON.stringify(merged),
  };
}

/**
 * Builds the Redux `piParityPaymentEventsLog` chain for `SecurePaymentService.logPiParityEvents`.
 *
 * @param {string} eventType
 * @param {ReadonlyArray<{ eventType: string; status: string }>} [prior]
 * @returns {{ record: { eventType: string; status: string; timestamp: number }; records: Array<{ eventType: string; status: string; timestamp: number }> }}
 */
export function getPayloadForPiParityEventLog(eventType, prior = []) {
  const priorList = Array.isArray(prior) ? prior : [];
  if (isBackendOnlyStatusEventType(eventType)) {
    return { record: null, records: priorList };
  }
  const now = Date.now();
  const last = priorList[priorList.length - 1];
  if (last && last.eventType === eventType) {
    if (isPaymentSelectionChangedEventType(eventType)) {
      return { record: last, records: priorList };
    }
    if (last.timestamp != null && now - Number(last.timestamp) < 1000) {
      return { record: last, records: priorList };
    }
  }
  const record = normalizePaymentEventRecord(
    {
      eventType,
      status: "completed",
      timestamp: now,
      eventId: `${eventType}_${now}_${priorList.length}`,
    },
    priorList.length,
  );
  return { record, records: [...priorList, record] };
}

/**
 * Maps commerce iframe postMessage payloads to business tracking events.
 *
 * @param {object} event — Parsed postMessage data object
 * @returns {ReadonlyArray<{ eventType: string; meta: object }>}
 *   Empty array when the payload does not match any tracked condition.
 */
export function mapEventToTracking(event) {
  const subType = event?.subType;
  const body = parseWalletNotificationBody(event);
  const bodyType = body?.type;
  const meta = pickTrackingMeta(body);

  if (subType === "AppLoaded") {
    const iframeApp = event?.data?.app ?? event?.app;
    return [
      {
        eventType: PAYMENT_MINI_APP_EVENTS.PAYMENT_MINI_APP_LOADED,
        meta: { ...meta, iframeApp },
      },
    ];
  }

  if (
    subType === "Notification" &&
    bodyType &&
    [
      PAYMENT_MINI_APP_EVENTS.ACCEPT_TERM_AND_CONDITIONS,
      PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED,
      PAYMENT_MINI_APP_EVENTS.PAYMENT_SUBMITTED,
      PAYMENT_MINI_APP_EVENTS.EXTERNAL_WALLET_REDIRECT_INITIATED,
      PAYMENT_MINI_APP_EVENTS.THREE_DS_REDIRECT_INITIATED,
      PAYMENT_MINI_APP_EVENTS.LPM_REDIRECT_INITIATED,
      PAYMENT_MINI_APP_EVENTS.PAYMENT_SUCCESS,
      PAYMENT_MINI_APP_EVENTS.PAYMENT_FAILURE,
      PAYMENT_MINI_APP_EVENTS.PAYMENT_CANCELLED,
    ].includes(bodyType)
  ) {
    const redirectedToVendorEvents = [
      PAYMENT_MINI_APP_EVENTS.EXTERNAL_WALLET_REDIRECT_INITIATED,
      PAYMENT_MINI_APP_EVENTS.THREE_DS_REDIRECT_INITIATED,
      PAYMENT_MINI_APP_EVENTS.LPM_REDIRECT_INITIATED,
    ];
    const formType =
      body && body.formType != null ? String(body.formType).trim() : "";
    const hasValidFormType =
      formType &&
      !["undefined", "null", "na", "n/a"].includes(formType.toLowerCase());
    if (
      bodyType === PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED &&
      !hasValidFormType
    ) {
      return [];
    }
    const eventType = redirectedToVendorEvents.includes(bodyType)
      ? PAYMENT_MINI_APP_EVENTS.REDIRECTED_TO_VENDOR
      : bodyType === PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED &&
        hasValidFormType
      ? `${PAYMENT_MINI_APP_EVENTS.PAYMENT_SELECTION_CHANGED}${PAYMENT_SELECTION_CHANGED_FORM_TYPE_SEPARATOR}${formType}`
      : bodyType;
    return [{ eventType, meta }];
  }

  return [];
}

